import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import { toast } from 'react-toastify';

import { useSearchQuery } from './location';

import { rootSiteSelector, siteFromRootSiteSelector } from 'selectors/root-site';
import { mapSearchQuery } from 'utils/url';
import { getErrorMessage } from 'utils/message';
import AxiosClient from 'configurations/api-client';
import { buildOrganizationsSearchPath } from 'constants/route-paths';
import { API_GET_ORGANIZATION_SCHEMA, API_SEARCH_ORGANIZATIONS, buildApiGetOrganization } from 'constants/api-paths';

export const useSearchingOrganizations = () => {
  const { siteSlug } = useParams();
  const { siteId } = useSelector(siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector);

  const query = useSearchQuery();
  const queryParams = mapSearchQuery(query);

  const { data, error, fetchNextPage, status, hasNextPage, isInitialLoading } = useInfiniteQuery(
    ['organizations', queryParams],
    async ({ pageParam = 1 }) => {
      const response = await AxiosClient.get(API_SEARCH_ORGANIZATIONS, {
        params: { ...queryParams, page: pageParam, pageSize: 12, siteId },
        data: null,
      });
      return response?.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 15 * 60 * 1_000,
      getNextPageParam: (lastPage) => {
        const { page, totalPages } = lastPage.pagination;
        return page < totalPages ? page + 1 : undefined;
      },
    },
  );

  const organizations = useMemo(() => flatMap(data?.pages, 'data'), [data]);

  const errorMessage = useMemo(() => (error?.response ? getErrorMessage(error.response) : ''), [error]);

  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage);
    }
  }, [errorMessage]);

  return useMemo(
    () => ({
      organizations,
      errorMessage,
      fetchNextPage,
      status,
      hasNextPage,
      isInitialLoading,
    }),
    [organizations, errorMessage, fetchNextPage, hasNextPage, isInitialLoading, status],
  );
};

export const useGetSiteOrganizationSchema = () => {
  const { siteSlug } = useParams();
  const { siteId } = useSelector(siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector);

  const query = useQuery({
    queryKey: ['organizationSchema', siteId],
    queryFn: async ({ signal }) => {
      const response = await AxiosClient.get(API_GET_ORGANIZATION_SCHEMA, {
        signal,
        params: { siteId },
      });

      return get(response, 'data', {});
    },
    enabled: !!siteId,
    refetchOnWindowFocus: false,
    staleTime: 15 * 60 * 1000,
  });

  const schema = get(query.data, 'schema', {});
  const template = get(query.data, 'template', []);

  return { schema, template };
};

export const useGetOrganization = (organizationSlug, options = {}) => {
  const { siteSlug } = useParams();

  const history = useHistory();
  const { disabledErrorHandler } = options;

  const { siteId } = useSelector(siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector);

  const query = useQuery({
    queryKey: ['sites', siteId, 'organizations', organizationSlug],
    queryFn: async ({ signal }) => {
      const response = await AxiosClient.get(buildApiGetOrganization(organizationSlug), {
        params: { siteId },
        signal,
      });

      return response.data;
    },
    onError: (error) => {
      if (disabledErrorHandler) return;
      toast.error(getErrorMessage(error.response));
      history.replace(buildOrganizationsSearchPath(siteSlug));
    },
    enabled: !!siteId && !!organizationSlug,
    refetchOnWindowFocus: false,
    staleTime: 15 * 60 * 1000,
    retry: false,
  });

  return query;
};
